import MediaElementAudioPlayer from './MediaElementAudioPlayer';
import Segmentable from './Segmentable';
import {
  AudioSegment,
  BaseAudioSegmentPlayer,
  BaseMediaElementAudioPlayer,
  DrawableAudio,
  DrawableAudioPlayer,
} from './types';
import WebAudioPlayer from './WebAudioPlayer';

// Safari and iOS have issues with sample rate when using MediaElement source
// https://bugs.webkit.org/show_bug.cgi?id=215314
//
// This bug results in distorted audio with a lot of popping and clicking.  As
// a workaround, this class creates a regular HTMLAudioElement that is used for
// audio playback while the underlying AudioPlayer's MediaSource node is muted
// and used to drive the visuals (i.e. waveform).
//
// The audio element in this class cannot be connected to the WebAudio graph, otherwise
// audio distortion will occur.
class AppleAudioPlayer
  extends MediaElementAudioPlayer
  implements DrawableAudio
{
  protected mutedPlayer: DrawableAudioPlayer & BaseMediaElementAudioPlayer;

  constructor(
    element: HTMLMediaElement,
    ctx?: AudioContext,
    waveformPlayer?: DrawableAudioPlayer & BaseMediaElementAudioPlayer,
  ) {
    super(element);

    this.mutedPlayer =
      waveformPlayer ??
      new WebAudioPlayer(element.cloneNode(true) as HTMLMediaElement, ctx);
    this.mutedPlayer.element.volume = 0;

    // shouldn't be necessary but a safeguard against playing unmuted audio on safari
    element.volume = 0;
    this.mutedPlayer.volume = 0;
  }

  public async play() {
    // when the browser is under load, these two things might not start at the same time.
    // first, start the waveform so that the user knows something is happening, even if
    // they can't hear the audio yet.  next, start the audio so the user can hear it. lastly,
    // re-sync the audio with the waveform
    const mutedPlay = this.mutedPlayer.play();
    const superPlay = super.play();

    await Promise.all([mutedPlay, superPlay]);
    this.mutedPlayer.seek(super.currentTime());
  }

  public pause() {
    this.mutedPlayer.pause();
    super.pause();
  }

  public async ready(): Promise<void> {
    this.mutedPlayer.ready();
    super.ready();
  }

  public seek(seconds: number) {
    this.mutedPlayer.seek(seconds);
    super.seek(seconds);
  }

  public get isReady() {
    // eslint-disable-next-line @typescript-eslint/dot-notation
    return this.mutedPlayer.isReady && super['isReady'];
  }

  public get waveformData() {
    return this.mutedPlayer.waveformData;
  }

  public get waveformDataSize() {
    return this.mutedPlayer.waveformDataSize;
  }
}

const AppleAudioSegmentPlayer = Segmentable(
  class SegmentableAudioPlayer
    extends AppleAudioPlayer
    implements BaseAudioSegmentPlayer
  {
    constructor(
      element: HTMLMediaElement,
      public segment: AudioSegment,
      ctx?: AudioContext,
    ) {
      super(element, ctx);
    }
  },
);

export default AppleAudioSegmentPlayer;
